import { Flow as _Flow } from './flow';

export namespace WhatsApp {

    /** Account messaging tier name */
    export enum PhoneMessagingTier {
        TIER_50 = 'TIER_50',
        TIER_250 = 'TIER_250',
        TIER_1K = 'TIER_1K',
        TIER_10K = 'TIER_10K',
        TIER_100K = 'TIER_100K',
        TIER_UNLIMITED = 'TIER_UNLIMITED',
    }

    export enum PhoneStatus {
        PENDING = 'PENDING',
        DELETED = 'DELETED',
        MIGRATED = 'MIGRATED',
        BANNED = 'BANNED',
        RESTRICTED = 'RESTRICTED',
        RATE_LIMITED = 'RATE_LIMITED',
        FLAGGED = 'FLAGGED',
        CONNECTED = 'CONNECTED',
        DISCONNECTED = 'DISCONNECTED',
        UNKNOWN = 'UNKNOWN',
        UNVERIFIED = 'UNVERIFIED',
    }

    export enum PhoneQualityScore {
        GREEN = 'GREEN',
        YELLOW = 'YELLOW',
        RED = 'RED',
    }

    /** Actual number of limit for messagigin tier names */
    export const PhoneMessagingTierValue = {
        [PhoneMessagingTier.TIER_50]: 50,
        [PhoneMessagingTier.TIER_250]: 250,
        [PhoneMessagingTier.TIER_1K]: 1000,
        [PhoneMessagingTier.TIER_10K]: 10_000,
        [PhoneMessagingTier.TIER_100K]: 100_000,
        [PhoneMessagingTier.TIER_UNLIMITED]: -1,
    } as const;

    export interface PhoneInfo {
        status?: PhoneStatus;
        messagingTier?: PhoneMessagingTier;
        qualityScore?: {
            score: PhoneQualityScore.RED | PhoneQualityScore.YELLOW;
            reasons?: string[];
        } | {
            score: PhoneQualityScore.GREEN;
        };
    }

    export interface Template {
        name: string;
        category: TemplateCategory | string;
        localizations: TemplateLocalization[];
        /** Any channel-specific metadata */
        metadata?: {
            [x: string]: any;
        };
    }

    export interface TemplateLocalization {
        status: TemplateLocalizationStatus;
        rejectionReason?: string;
        language: string;
        components: TemplateComponent[];
        /** Any channel-specific metadata */
        metadata?: {
            [x: string]: any;
        };
    }

    export type TemplateComponent = TemplateHeaderComponent | TemplateBodyComponent | TemplateFooterComponent | TemplateButtonsComponent;

    export type TemplateHeaderComponent = {
        type: TemplateComponentType.HEADER;
    } & (
        {
            format: TemplateComponentFormat.TEXT;
            text: string;
            example?: {
                header_text: string[];
                header_handle?: undefined;
            };
        } | {
            format: TemplateComponentFormat.IMAGE | TemplateComponentFormat.VIDEO | TemplateComponentFormat.DOCUMENT | TemplateComponentFormat.LOCATION;
            example?: {
                /**
                 * URLs of file examples
                 *
                 * @todo Rewrite API to handle stored files through IDs
                 */
                header_handle: string[];
                header_text?: undefined;
            };
        }
    );

    export interface TemplateBodyComponent {
        type: TemplateComponentType.BODY;
        text: string;
        example?: {
            body_text: string[][];
        };
    }

    export interface TemplateFooterComponent {
        type: TemplateComponentType.FOOTER;
        text: string;
    }

    export interface TemplateButtonsComponent {
        type: TemplateComponentType.BUTTONS;
        /**
         * Buttons list
         *
         * **NOTE**: Here only could be 2 of TemplateURLButton or TemplatePhoneButton,
         * and no TemplateQuickReplyButton if the former present,
         * or up to 3 TemplateQuickReplyButton.
         * But making this type more strict makes it hard to use - methods like ```map``` and ```filter```
         * shows error.
         */
        buttons: Array<TemplateURLButton | TemplatePhoneButton | TemplateQuickReplyButton>;
    }

    export interface TemplateQuickReplyButton {
        type: TemplateButtonType.QUICK_REPLY;
        text: string;
    }

    export interface TemplateURLButton {
        type: TemplateButtonType.URL;
        text: string;
        url: string;
        example?: {
            url: string;
        };
    }

    export interface TemplatePhoneButton {
        type: TemplateButtonType.PHONE_NUMBER;
        text: string;
        phone_number: string;
    }

    export enum TemplateButtonType {
        QUICK_REPLY = 'QUICK_REPLY',
        URL = 'URL',
        PHONE_NUMBER = 'PHONE_NUMBER',
    }

    export enum TemplateComponentType {
        HEADER = 'HEADER',
        BODY = 'BODY',
        FOOTER = 'FOOTER',
        BUTTONS = 'BUTTONS',
    }

    export enum TemplateComponentFormat {
        TEXT = 'TEXT',
        IMAGE = 'IMAGE',
        DOCUMENT = 'DOCUMENT',
        VIDEO = 'VIDEO',
        LOCATION = 'LOCATION',
    }

    export enum TemplateCategory {
        UTILITY = 'UTILITY',
        MARKETING = 'MARKETING',
        AUTHENTICATION = 'AUTHENTICATION',
    }

    export enum TemplateLocalizationStatus {
        REQUESTED = 'REQUESTED',
        SUBMIT_FAILED = 'SUBMIT_FAILED',
        PENDING = 'PENDING',
        APPROVED = 'APPROVED',
        REJECTED = 'REJECTED',
        DELETION_PENDING = 'DELETION_PENDING',
        DELETED = 'DELETED',
        SUBMITTED = 'SUBMITTED',
    }

    export const supportedLocalizations = Object.freeze({
        he: 'Hebrew',
        en: 'English',
        ar: 'Arabic',
        es: 'Spanish',
        af: 'Afrikaans',
        sq: 'Albanian',
        az: 'Azerbaijani',
        bn: 'Bengali',
        bg: 'Bulgarian',
        ca: 'Catalan',
        zh_CN: 'Chinese (CHN)',
        zh_HK: 'Chinese (HKG)',
        zh_TW: 'Chinese (TAI)',
        hr: 'Croatian',
        cs: 'Czech',
        da: 'Danish',
        nl: 'Dutch',
        en_GB: 'English (UK)',
        en_US: 'English (US)',
        et: 'Estonian',
        fil: 'Filipino',
        fi: 'Finnish',
        fr: 'French',
        ka: 'Georgian',
        de: 'German',
        el: 'Greek',
        gu: 'Gujarati',
        ha: 'Hausa',
        hi: 'Hindi',
        hu: 'Hungarian',
        id: 'Indonesian',
        ga: 'Irish',
        it: 'Italian',
        ja: 'Japanese',
        kn: 'Kannada',
        kk: 'Kazakh',
        rw_RW: 'Kinyarwanda',
        ko: 'Korean',
        ky_KG: 'Kyrgyz (Kyrgyzstan)',
        lo: 'Lao',
        lv: 'Latvian',
        lt: 'Lithuanian',
        mk: 'Macedonian',
        ms: 'Malay',
        ml: 'Malayalam',
        mr: 'Marathi',
        nb: 'Norwegian',
        fa: 'Persian',
        pl: 'Polish',
        pt_BR: 'Portuguese (BR)',
        pt_PT: 'Portuguese (POR)',
        pa: 'Punjabi',
        ro: 'Romanian',
        ru: 'Russian',
        sr: 'Serbian',
        sk: 'Slovak',
        sl: 'Slovenian',
        es_AR: 'Spanish (ARG)',
        es_ES: 'Spanish (SPA)',
        es_MX: 'Spanish (MEX)',
        sw: 'Swahili',
        sv: 'Swedish',
        ta: 'Tamil',
        te: 'Telugu',
        th: 'Thai',
        tr: 'Turkish',
        uk: 'Ukrainian',
        ur: 'Urdu',
        uz: 'Uzbek',
        vi: 'Vietnamese',
        zu: 'Zulu',
    });

    export enum Feature {
        templateLocalizationEditable = 'templateLocalizationEditable',
    }

    export enum TemplateHeaderSendType {
        IMAGE = 'image',
        VIDEO = 'video',
        DOCUMENT = 'document',
        TEXT = 'text',
    }

    export enum TemplateButtonPayloadType {
        QUICK_REPLY = 'quick_reply',
        URL = 'url',
    }

    export type TemplateSendContents = {
        header?: {
            type: TemplateHeaderSendType.IMAGE | TemplateHeaderSendType.VIDEO;
            url: string;
        } | {
            type: TemplateHeaderSendType.DOCUMENT;
            url: string;
            filename?: string;
        } | {
            type: TemplateHeaderSendType.TEXT;
            text: string[];
        };
        body?: string[];
        buttons?: Array<{
            type: TemplateButtonPayloadType;
            payload: string;
        }>;
    };

    export type TemplateEmulationOptions = {
        /** Do not include header contents */
        omitHeader?: boolean;
        /** Do not include footer contents */
        omitFooter?: boolean;
    };

    /**
     * WhatsApp-specific structured message contents that not supported by common sending interface
     */
    export type MessageSpecialContents = {
        header?: MessageInteractiveHeader;
        /**
         * Could be attached to interactive message
         */
        footer?: string;
        /**
         * WhatsApp Flow submitted data
         */
        flow_reply?: MessageSpecialFlowReply;
        /**
         * Outgoing session message with a button to start a Flow
         */
        flow_cta?: MessageSpecialFlowCTA;
    };

    export type MessageSpecialFlowCTA = {
        /**
         * Flow ID
         */
        id: string;
        /**
         * CTA button text
         */
        cta: string;
        /**
         * A token that is generated by the business to serve as an identifier.
         */
        token: string;
        /**
         * Flow mode. Must match the Flow current mode
         */
        mode?: _Flow.Mode;
        action?: _Flow.Action.navigate | _Flow.Action.data_exchange;
        /**
         * NOTE: this object shape is different for different `action` values,
         * but this type simplified to avoid complex type definitions.
         */
        payload?: {
            /**
             * Flow screen name to navigate to.
             * Required if `action` is `navigate`, otherwise should not be specified.
             */
            screen?: string;
            /**
             * The input data for the first screen of the Flow. Must be a non-empty object.
             */
            data?: {
                [x: string]: unknown;
            };
        };
    };

    export type MessageSpecialFlowReply = {
        /**
         * Token specified during sending Flow CTA message
         */
        token: string;
        /**
         * Any data defined by the flow. Multilevel object is allowed.
         */
        data?: {
            [x: string]: unknown;
        };
    };

    /**
     * Could be attached to interactive message
     */
    export type MessageInteractiveHeader = {
        type: 'text';
        text: string;
    } | {
        type: 'image' | 'video';
        url: string;
    } | {
        type: 'document';
        url: string;
        filename?: string;
    };

    export import Flow = _Flow;
}
